(ns manage.campaign-tags-page.view
  (:require ["@mui/icons-material/CancelRounded$default" :as CancelRoundedIcon]
            ["@mui/icons-material/CheckCircleRounded$default" :as CheckCircleRoundedIcon]
            ["@mui/icons-material/Refresh$default" :as RefreshIcon]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.string :as str]
            [common.db.effects.http]
            [common.display-tag :refer [display-tag]]
            [common.widgets.overlay-page :as overlay-page]
            [common.widgets.page :as page]
            [manage.adset.util :as adset-util]
            [manage.campaign-tags-page.db]
            [manage.export-display-tag-ocp.db]
            [manage.export-display-tag-ocp.view]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def available-tag-types
  [{:label "Ins" :value "ins"}
   {:label "JS" :value "js"}
   {:label "CM360" :value "cm360"}])

(defui setting-label [{:keys [label]}]
  ($ Typography
     {:variant "h5"
      :pr 1}
     label))

(defui export-status-chip [{:keys [status icon]}]
  ($ Chip
     (cond-> {:label status
              :variant "subtle"
              :size "small"
              :sx #js {:fontWeight 700
                       :textTransform "uppercase"}}
       (= status "Ready") (assoc :color "default")
       (= status "Success") (assoc :color "success")
       (= status "Failed") (assoc :color "error")
       (= status "Pending") (assoc :color "default")
       (= status "Exporting") (assoc :color "primary")
       icon (assoc :icon icon))))

(defui setting-checkbox [{:keys [attribute-id label]}]
  ($ FormControlLabel
     {:label label
      :control ($ Checkbox
                  {:id (name attribute-id)
                   :data-testid (name attribute-id)
                   :checked (or (use-subscribe [:campaign-tags.page.settings/value attribute-id]) false)
                   :onChange (fn [^js event]
                               (rf/dispatch [:campaign-tags.page.settings/set-value attribute-id (-> event .-target .-checked)]))})}))

(defui settings-select [{:keys [label attribute-id options]}]
  ($ Stack
     {:direction "row"
      :alignItems "center"}
     ($ setting-label {:label label})
     ($ Select
        {:data-testid (name attribute-id)
         :size "small"
         :sx #js {:minWidth 100}
         :MenuProps {;; Keeps the backdrop so clicking outside the select closes the menu,
                     ;; but makes it invisible.
                     :sx #js {".MuiBackdrop-root" {:backgroundColor "rgba(255, 255, 255, 0)"}}}
         :value (or (use-subscribe [:campaign-tags.page.settings/value attribute-id]) "")
         :onChange (fn [^js event]
                     (rf/dispatch [:campaign-tags.page.settings/set-value attribute-id (-> event .-target .-value)]))}
        (for [{:keys [label value]} options]
          ($ MenuItem {:key value :value value} label)))))

(defui select-all-cm360-checkbox []
  (let [cm360-row-ids (use-subscribe [:campaign-tags.page.selection/cm360-row-ids])
        selected-row-ids (use-subscribe [:campaign-tags.page.selection/selected-row-ids])
        cm360-count (count cm360-row-ids)
        selected-cm360-count (count (filter (set selected-row-ids) cm360-row-ids))
        all-selected? (and (pos? cm360-count) (= selected-cm360-count cm360-count))
        some-selected? (pos? selected-cm360-count)]
    (when (pos? cm360-count)
      ($ FormControlLabel
         {:label (str "Select All CM360 (" cm360-count ")")
          :control ($ Checkbox
                      {:checked all-selected?
                       :indeterminate (and some-selected? (not all-selected?))
                       :onChange (fn [^js _event]
                                   (rf/dispatch [:campaign-tags.page.selection/toggle-all-cm360]))
                       :size "small"})}))))

(defui settings-section []
  ($ Stack
     {:direction "row"
      :alignItems "center"
      :sx #js {:my 3}}
     ;; Left-hand side
     ($ Stack
        {:direction "row"
         :alignItems "center"
         :spacing 2
         :sx #js {:flex 1}}
        ($ Stack
           {:direction "row"
            :alignItems "center"}
           ($ setting-label {:label "Show:"})
           ($ setting-checkbox {:attribute-id :video :label "Video"})
           ($ setting-checkbox {:attribute-id :display :label "Display"}))
        ($ Stack
           {:direction "row"
            :alignItems "center"}
           ($ setting-label {:label "Preview:"})
           ($ setting-checkbox {:attribute-id :preview :label "Show"}))
        ($ select-all-cm360-checkbox))

     ;; Right-hand side
     ($ Stack
        {:direction "row"
         :alignItems "center"
         :spacing 2}
        ($ settings-select
           {:label "DSP:"
            :attribute-id :dsp
            :options (into [{:label "All" :value "all"}]
                           (map (fn [dsp] {:label dsp :value dsp}))
                           (use-subscribe [:campaign-tags.page/all-tag-dsps]))})
        ($ settings-select
           {:label "Dimensions:"
            :attribute-id :dimension
            :options (into [{:label "All" :value "all"}]
                           (map (fn [size] {:label size :value size}))
                           (use-subscribe [:campaign-tags.page/all-tag-sizes]))})
        ($ settings-select
           {:label "Tag type:"
            :attribute-id :display-tag-type
            :options available-tag-types}))))

(defui grid-cell-selection [{:keys [row-id]}]
  (let [data-by-row-id (use-subscribe [:campaign-tags.page/data-by-row-id])
        is-selected (use-subscribe [:campaign-tags.page.selection/is-row-selected row-id])
        tag (-> row-id data-by-row-id :tag)
        has-cm360? (some? (:cm360 tag))]
    ($ Box
       {:sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :p 0.5
                 :display "flex"
                 :justifyContent "center"
                 :alignItems "center"}}
       (when has-cm360?
         ($ Checkbox
            {:checked is-selected
             :onChange (fn [^js _event]
                         (rf/dispatch [:campaign-tags.page.selection/toggle-row row-id]))
             :size "small"})))))

(defui grid-cell-export-status [{:keys [row-id]}]
  (let [export-status (use-subscribe [:campaign-tags.page.export-status/row-status row-id])
        data-by-row-id (use-subscribe [:campaign-tags.page/data-by-row-id])
        tag (-> row-id data-by-row-id :tag)
        has-cm360? (some? (:cm360 tag))
        display-status (or export-status (when has-cm360? :ready))]
    ($ Box
       {:sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :p 0.5
                 :display "flex"
                 :alignItems "center"
                 :justifyContent "flex-end"}}
       (when has-cm360?
         (case display-status
           :ready ($ export-status-chip
                     {:status "Ready"})
           :in-progress ($ export-status-chip
                           {:status "Exporting"
                            :icon ($ CircularProgress {:size 16})})
           :success ($ export-status-chip
                       {:status "Success"
                        :icon ($ CheckCircleRoundedIcon)})
           :failed ($ Stack
                      {:direction "row"
                       :spacing 0.5
                       :alignItems "center"}
                      ($ export-status-chip
                         {:status "Failed"
                          :icon ($ CancelRoundedIcon)})
                      ($ Tooltip
                         {:title "Retry export"}
                         ($ IconButton
                            {:size "small"
                             :onClick (fn [^js _event]
                                        (rf/dispatch [:campaign-tags.page/retry-single-export row-id]))}
                            ($ RefreshIcon {:fontSize "small"}))))
           nil)))))

(defui grid-cell-adset-name [{:keys [row-id]}]
  (let [adset-channel (use-subscribe [:campaign-tags.page/grid-row-adset-channel row-id])
        adset-name (use-subscribe [:campaign-tags.page/grid-row-adset-name row-id])]
    ($ Stack
       {:direction "row"
        :alignItems "center"
        :sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :p 0.5
                 :minWidth 0}} ; Allow flex items to shrink below their content size
       ($ adset-util/channel-icon {:channel adset-channel})
       ($ Tooltip
          {:title adset-name
           :placement "top"}
          ($ Typography
             {:variant "h5"
              :sx #js {:px 1
                       :overflow "hidden"
                       :textOverflow "ellipsis"
                       :whiteSpace "nowrap"
                       :minWidth 0 ; Allow text to shrink
                       :flex 1}} ; Take remaining space in the stack
             adset-name)))))

(defui grid-cell-dsp [{:keys [row-id]}]
  (let [dsp (use-subscribe [:campaign-tags.page/grid-row-dsp row-id])]
    ($ Box
       {:sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :p 0.5
                 :display "flex"
                 :alignItems "center"}}
       ($ Typography
          {:variant "h5"
           :sx #js {:color "text.secondary"
                    :px 0.5}}
          dsp))))

(defui grid-cell-size [{:keys [row-id]}]
  (let [adset-channel (use-subscribe [:campaign-tags.page/grid-row-adset-channel row-id])
        {:keys [size]} (use-subscribe [:campaign-tags.page/grid-row-dimensions row-id])
        label (if (and (= adset-channel "display") (some? size))
                size
                "n/a")]
    ($ Box
       {:sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :p 0.5
                 :display "flex"
                 :alignItems "center"}}
       ($ Typography
          {:variant "h5"
           :sx #js {:color "text.secondary"
                    :px 0.5}}
          label))))

(def vast-ad-type->label
  {"vast-linear"    "VAST"
   "vast-aws-prime" "VAST Amazon"})

(defui grid-cell-tag [{:keys [row-id]}]
  (let [display-tag-type (use-subscribe [:campaign-tags.page.settings/grid-row-display-tag-type row-id])
        tag-version (use-subscribe [:campaign-tags.page.settings/grid-row-display-tag-version row-id])
        info-text (use-subscribe [:campaign-tags.page/grid-row-tag-info row-id])
        tag-text (use-subscribe [:campaign-tags.page/grid-row-tag-text row-id])
        adset-channel (use-subscribe [:campaign-tags.page/grid-row-adset-channel row-id])
        row-dsp (use-subscribe [:campaign-tags.page/grid-row-dsp row-id])
        vast-ad-type (use-subscribe [:campaign-tags.page/grid-row-vast-ad-type row-id])]
    ($ Box
       {:sx #js {:borderColor "grey.60"
                 :borderRightWidth "1px"
                 :borderTopWidth "1px"
                 :gridColumnEnd "span 5"
                 :width "500px"}}
       ($ Paper
          {:elevation 0
           :sx #js {:m 1
                    :p 1
                    :borderRadius 2
                    :backgroundColor "grey.80"}}
          ($ Stack
             {:direction "row"
              :alignItems "center"}
             (when-not (str/blank? info-text)
               ($ Tooltip
                  {:placement "bottom"
                   :title info-text}
                  ($ Icon {:color "warning"} "warning")))
             ($ Stack
                {:sx #js {:flexGrow 1}
                 :direction "row"
                 :justifyContent "flex-end"}

                ;; THEMING: The following buttons could be implemented using MUI's ToggleButtons instead.
                (case adset-channel
                  ("video" "ctv")
                  ($ :<>
                     (if (= row-dsp "Xandr")
                       ($ :<>
                          ;; "Secure" and "Insecure" buttons
                          ($ Button
                             {:size "small"
                              :variant "text"
                              :sx #js {:minWidth "40px"
                                       :fontSize "14px"
                                       :opacity (if (= display-tag-type "secure") 1 0.4)}
                              :onClick (fn []
                                         (rf/dispatch [:campaign-tags.page.settings/set-grid-row-display-tag-type row-id "secure"]))}
                             "Secure")
                          ($ Button
                             {:size "small"
                              :variant "text"
                              :sx #js {:minWidth "40px"
                                       :fontSize "14px"
                                       :opacity (if (= display-tag-type "insecure") 1 0.4)}
                              :onClick (fn []
                                         (rf/dispatch [:campaign-tags.page.settings/set-grid-row-display-tag-type row-id "insecure"]))}
                             "Insecure"))

                       ($ Button
                          {:size "small"
                           :variant "text"
                           :sx #js {:minWidth "40px"
                                    :fontSize "14px"
                                    ;; Makes the button look non-clickable.
                                    :cursor "default"
                                    :pointerEvents "none"}}
                          (or (get vast-ad-type->label vast-ad-type) "VAST")))
                     ($ Button
                        {:size "small"
                         :variant "text"
                         :sx #js {:minWidth "40px"
                                  :fontSize "14px"
                                  :opacity (if (not= tag-version :4.0) 1 0.4)}
                         :onClick #(rf/dispatch [:campaign-tags.page.settings/set-vast-version row-id :2.0])}
                        "2.0")
                     (when-not vast-ad-type
                       ($ Button
                          {:size "small"
                           :variant "text"
                           :sx #js {:minWidth "40px"
                                    :fontSize "14px"
                                    :opacity (if (= tag-version :4.0) 1 0.4)}
                           :onClick #(rf/dispatch [:campaign-tags.page.settings/set-vast-version row-id :4.0])}
                          "4.0")))

                  "display"
                  ($ :<>
                     ;; "Ins" and "JS" buttons
                     ($ Button
                        {:size "small"
                         :variant "text"
                         :sx #js {:minWidth "40px"
                                  :fontSize "14px"
                                  :opacity (if (= display-tag-type "ins") 1 0.4)}
                         :onClick (fn []
                                    (rf/dispatch [:campaign-tags.page.settings/set-grid-row-display-tag-type row-id "ins"]))}
                        "INS")
                     ($ Button
                        {:size "small"
                         :variant "text"
                         :sx #js {:minWidth "40px"
                                  :fontSize "14px"
                                  :opacity (if (= display-tag-type "js") 1 0.4)}
                         :onClick (fn []
                                    (rf/dispatch [:campaign-tags.page.settings/set-grid-row-display-tag-type row-id "js"]))}
                        "JS")
                     ($ Button
                        {:size "small"
                         :variant "text"
                         :sx #js {:minWidth "40px"
                                  :fontSize "14px"
                                  :opacity (if (= display-tag-type "cm360") 1 0.4)}
                         :onClick (fn []
                                    (rf/dispatch [:campaign-tags.page.settings/set-grid-row-display-tag-type row-id "cm360"]))}
                        "CM360")))

                ;; Clipboard copy button
                ($ IconButton
                   {:onClick (fn []
                               ;; rf/dispatch-sync is needed
                               (rf/dispatch-sync [:campaign-tags.page/copy-to-clipboard tag-text]))}
                   ($ Icon "content_copy"))))

          ;; The tag's text
          ($ Typography
             {:sx #js {:mt 1
                       :color "grey.30"
                       :overflowX "auto"}}
             tag-text)))))

(defui grid-cell-preview [{:keys [row-id]}]
  (let [{:keys [width height]} (use-subscribe [:campaign-tags.page/grid-row-dimensions row-id])
        tag-text (use-subscribe [:campaign-tags.page/grid-row-tag-text row-id])
        adset-channel (use-subscribe [:campaign-tags.page/grid-row-adset-channel row-id])]
    ($ Box {:sx #js {:borderColor "grey.60"
                     :borderRightWidth "1px"
                     :borderTopWidth "1px"
                     :gridRowEnd "span 2"
                     :p 1}}

       (when (= adset-channel "display")
         ($ display-tag {:tag-text tag-text :width width :height height})))))

(defui tag-grid-row [{:keys [row-id]}]
  (let [preview-shown (use-subscribe [:campaign-tags.page.settings/value :preview])]
    ($ Box {:sx #js {:display "grid"
                     :gridTemplateColumns (str "min-content 200px min-content min-content 1fr"
                                               (when preview-shown " min-content"))
                     :gridTemplateRows "min-content 1fr"
                     :width "min-content"
                     :borderColor "grey.60"
                     :borderLeftWidth "1px"
                     :borderBottomWidth "1px"}}
       ($ grid-cell-selection {:row-id row-id})
       ($ grid-cell-adset-name {:row-id row-id})
       ($ grid-cell-dsp {:row-id row-id})
       ($ grid-cell-size {:row-id row-id})
       ($ grid-cell-export-status {:row-id row-id})
       (when preview-shown
         ($ grid-cell-preview {:row-id row-id}))
       ($ grid-cell-tag {:row-id row-id}))))

(defui grid []
  (let [row-ids (use-subscribe [:campaign-tags.page/filtered-row-ids])]
    ($ Box {:sx #js {:overflowX "auto"}}
       (for [row-id row-ids]
         ($ tag-grid-row {:key row-id :row-id row-id})))))

(defhook use-campaign-tags-page-props []
  (let [campaign-tags-id (:campaign-tags (use-subscribe [:router/query-parameters]))
        on-close (uix/use-callback
                  (fn []
                    (rfe/set-query (fn [query-params]
                                     (dissoc query-params :campaign-tags))))
                  [])]
    {:open? (int? campaign-tags-id)
     :onClose on-close
     :campaignTagsId campaign-tags-id}))

(def campaign-tags-page
  (uix/as-react
   (fn [{:keys [ref open? onClose campaignTagsId]}]
     (let [state (use-subscribe [:resource.campaign-tags/page-state])
           selected-cm360-count (use-subscribe [:campaign-tags.page.selection/selected-cm360-count])
           has-selected? (pos? selected-cm360-count)]
       (uix/use-effect (fn []
                         (when campaignTagsId
                           (rf/dispatch [:campaign-tags.page/get-page campaignTagsId]))
                         (fn []
                           (rf/dispatch [:campaign-tags.page/cleanup])))
                       [campaignTagsId])
       (when open?
         ($ overlay-page/overlay-page {:ref ref}
            (case state
              :success ($ :<>
                          ($ overlay-page/header {:on-close onClose :title "Tags"}
                             ($ Stack
                                {:direction "row"
                                 :alignItems "center"
                                 :spacing 1}
                                ($ Button
                                   {:variant "text"
                                    :startIcon ($ Icon "cloud_download")
                                    :onClick (fn [^js _event]
                                               (rf/dispatch [:campaign-tags.page/export-excel-file]))}
                                   "Generate excel file")
                                ($ Button
                                   {:variant "text"
                                    :startIcon ($ Icon "cloud_download")
                                    :disabled (not has-selected?)
                                    :onClick (fn [^js _event]
                                               (rf/dispatch [:campaign-tags.page/export-display-tag]))}
                                   (if has-selected?
                                     (str "Export " selected-cm360-count " CM360 Tags")
                                     "Select CM360 Tags to Export"))
                                ($ Button
                                   {:variant "text"
                                    :startIcon ($ Icon "save")
                                    :onClick (fn [^js _event]
                                               (rf/dispatch [:campaign-tags.page/save]))}
                                   "Save")
                                ($ Button
                                   {:variant "text"
                                    :onClick onClose
                                    :sx #js {:color "grey.10"
                                             :fontWeight 500}}
                                   "Close")))
                          ($ page/content-padding
                             ($ settings-section)
                             ($ Typography {:variant "h3" :my 2} "Tags")
                             ($ grid)))
              :pending ($ Stack
                          {:justifyContent "center"
                           :alignItems "center"
                           :sx #js {:flex 1}}
                          ($ CircularProgress))
              nil)))))))
